import {
    CoreApplicationContext,
    CoreGoogleAnalyticsSettings,
} from '@eb/context-gen';
import { setLanguage } from '@eb/i18n';
import { useRealUserMonitoring } from '@eb/real-user-monitoring';
import { trackAnalyticsEvent } from '@eb/site-analytics';
import { SiteStructure } from '@eb/site-structure';
import { useConstructor } from '@eb/use-constructor';
import React from 'react';
import { Header } from './components/Header';
import { Main } from './components/Main';
import {
    GA_ACTION_PROFILE_DROPDOWN,
    GA_CATEGORY_HEADER,
    GA_LABEL_CREATOR_EVENT,
    ORGANIZER_PROFILE_RUM_SAMPLE_RATE,
} from './constants';
import { EnvContext, OrganizerContext, UserProvider } from './context';
import {
    DiscoveryEventsFromView,
    Organizer,
    OrganizerCollection,
    OrganizerCollections,
} from './types';

interface ViewData {
    website: string;
    page: {
        background_color: string;
        text_color: string;
    };
    events: DiscoveryEventsFromView;
}

export interface AppProps {
    api_data: {
        organizer: Organizer;
        organizer_collections: OrganizerCollections<OrganizerCollection>;
    };
    view_data: ViewData;
    facebookOptions: {
        appId: string;
        version: string;
        xfbml: boolean;
    };
    captchaKeyV2: string;
    featureFlags?: object;
    hasPermissionToEditOrganizationInfo: boolean;
}

export const App: React.FunctionComponent<
    CoreApplicationContext & AppProps & CoreGoogleAnalyticsSettings
> = (props) => {
    const headerProps = {
        organizer: props.api_data.organizer,
        website: props.view_data.website,
        customStyle: {
            text_color: props.view_data.page.text_color,
            background_color: props.view_data.page.background_color,
        },
        hasPermissionToEditOrganizationInfo:
            props.hasPermissionToEditOrganizationInfo,
    };

    const mainProps = {
        organizer: props.api_data.organizer,
        events: props.view_data.events,
        organizerCollections: props.api_data.organizer_collections,
    };
    const onTrackingUserDropdown = () => {
        trackAnalyticsEvent({
            action: GA_ACTION_PROFILE_DROPDOWN,
            category: GA_CATEGORY_HEADER,
            label: GA_LABEL_CREATOR_EVENT,
        });
    };

    const sampleRate =
        props.env.ebDomain.indexOf('eventbrite') > -1
            ? ORGANIZER_PROFILE_RUM_SAMPLE_RATE
            : 0;

    useRealUserMonitoring({
        sampleRate,
        version: props.app_version,
        service: props.app_name,
    });

    useConstructor(() => {
        setLanguage(props.env.localeInfo.locale);
    });

    return (
        <SiteStructure
            env={props.env}
            request={props.request}
            user={props.user}
            footerLinks={props.footerLinks}
            fullScreenMain={true}
            featureFlags={props.featureFlags}
            onMouseEnterGlobalHeader={onTrackingUserDropdown}
        >
            <UserProvider user={props.user}>
                <OrganizerContext.Provider value={props.api_data.organizer}>
                    <EnvContext.Provider
                        value={{
                            gaSettings: props.gaSettings,
                            env: props.env,
                            facebookOptions: props.facebookOptions,
                            captchaKey: props.captchaKeyV2,
                        }}
                    >
                        <Header {...headerProps} />
                        <Main {...mainProps} />
                    </EnvContext.Provider>
                </OrganizerContext.Provider>
            </UserProvider>
        </SiteStructure>
    );
};
